<template src="./reviews-list-widget.html"></template>

<script lang="ts">
import { debounce } from 'util/ts-utils'
import domUtils from 'util/dom-utils'
import { Review } from 'services/api-service'
import reviewsListConfig, { RelatedReviewsDisplayTypes } from './reviews-list-widget.config'
import reviewsStore from 'store/reviews-store'
import RelatedReviewsList from 'components/reviews-list/RelatedReviewsList.vue'
import ReviewsList from 'components/reviews-list/ReviewsList.vue'
import globalStore, { Store } from 'init/global-store'
import UserToolbar from 'components/user-toolbar/UserToolbar.vue'
import formatName from 'filters/format-name'
import visibleObserver from 'util/visible-observer'

export default {
  data() {
    return {
      catalogItemTitle: '',
      relatedReviewsJsonLdReady: false,
      reviews: [],
      relatedReviews: [],
      reviewsStore,
      globalStoreState: globalStore.state,
      showFullReview: !reviewsListConfig.enableLazyLoading,
      widgetIsInitiallyHidden: false
    }
  },
  components: {
    'tt-related-reviews-list': RelatedReviewsList,
    'tt-reviews-list': ReviewsList,
    'tt-user-toolbar': UserToolbar
  },
  computed: {
    currentSku(): string {
      return (this.globalStoreState as Store).sku
    },
    showRelatedList() {
      return reviewsListConfig.relatedReviewsDisplay === RelatedReviewsDisplayTypes.SEPARATE
    },
    showUserToolbar() {
      return reviewsListConfig.displayUserToolbar
    },
    showSearchResults() {
      return this.reviewsStore.searchTerm
    },
    jsonLdReady() {
      return this.catalogItemTitle.length > 0 // reviews has emitted
        // related reviews have emitted or not present
        && (this.relatedReviewsJsonLdReady || !this.showRelatedList)
        // There are reviews to include
        && (this.reviews.length > 0 || this.relatedReviews.length > 0)
    }
  },
  methods: {
    loadJsonLd: debounce(function () {
      if (this.jsonLdReady) {
        const jsonLdData = {
          '@context': 'http://schema.org',
          '@type': 'Product',
          '@id':  window.location.href,
          name: this.catalogItemTitle,
          review: []
        }
        this.reviews.forEach((review) => {
          jsonLdData.review.push(this.buildJsonLdReview(review))
        })
        this.relatedReviews.forEach((review) => {
          jsonLdData.review.push(this.buildJsonLdReview(review))
        })

        const script = document.createElement('script')
        script.type = 'application/ld+json'
        script.innerHTML = JSON.stringify(jsonLdData)

        document.head.appendChild(script)
      }
    }, 10),
    buildJsonLdReview(review: Review) {
      return {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: formatName(review.user)
        },
        datePublished: new Date(review.dateCreatedMillis).toISOString(),
        description: review.text,
        name: review.title,
        reviewRating: {
          '@type': 'Rating',
          bestRating: '5',
          ratingValue: `${review.rating}`,
          worstRating: '1'
        }
      }
    },
    openMediaModal(media, startingIndex = 0) {
      // eslint-disable-next-line max-len
      import(/* webpackChunkName: 'media-modal' */ 'components/media-modal/media-modal-loader').then((mediaModalLoader) => {
        mediaModalLoader.default.load({ media, startingIndex })
      })
    },
    relatedReviewsLoaded(relatedReviews) {
      this.relatedReviewsJsonLdReady = true
      this.relatedReviews = relatedReviews
      this.loadJsonLd()
    },
    reviewsLoaded(reviews, catalogItemTitle, jsonLdEnabled) {
      this.catalogItemTitle = catalogItemTitle
      this.reviews = reviews
      if (jsonLdEnabled) {
        this.loadJsonLd()
      }
    }
  },
  created() {
    // used by both direct and related reviews lists, so setting here
    reviewsStore.sort = reviewsListConfig.defaultSortOption
  },
  mounted() {
    if (reviewsListConfig.enableLazyLoading) {
      const rootElem = this.$el
      visibleObserver.register(rootElem, () => {
        this.showFullReview = true
        visibleObserver.unregister(rootElem)
      })
      // if the widget is not visible, hidden under a tab for example,
      // then set widgetIsInitiallyHidden to true so the BasicReview content
      // will be included in the page
      domUtils.whenElementIsVisible(() => this.$el, 50).catch(() => {
        this.widgetIsInitiallyHidden = true
      })
    }
  }
}
</script>

<style lang="scss" src="./reviews-list-widget.scss"></style>
